import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material'
import { apiClient } from '../services/apiService'

interface SavedView {
  id: string
  name: string
  description?: string
  filters: Record<string, any>
  sortConfig?: {
    key: string | null
    direction: 'asc' | 'desc'
  }
  searchText?: string
  isDefault: boolean
  tags: string[]
  createdAt: string
  updatedAt: string
}

interface SavedViewsProps {
  currentFilters: Record<string, any>
  currentSort?: {
    key: string | null
    direction: 'asc' | 'desc'
  }
  currentSearch?: string
  onLoadView: (view: SavedView) => void
}

const SavedViews: React.FC<SavedViewsProps> = ({
  currentFilters,
  currentSort,
  currentSearch,
  onLoadView
}) => {
  // Extract teams from current filters for display
  const selectedTeams = currentFilters.teams || []

  const [views, setViews] = useState<SavedView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  
  // View menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedView, setSelectedView] = useState<SavedView | null>(null)

  useEffect(() => {
    fetchViews()
  }, [])

  const fetchViews = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.get('/saved-views')
      const data = response.data
      if (!data.success) {
        throw new Error(data.message || 'Failed to load saved views')
      }

      setViews(data.data || [])
    } catch (err) {
      console.error('Error fetching saved views:', err)
      setError(err instanceof Error ? err.message : 'Failed to load saved views')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveView = async () => {
    if (!saveName.trim()) {
      setSaveError('View name is required')
      return
    }

    try {
      setSaveError(null)

      const viewData = {
        name: saveName.trim(),
        description: saveDescription.trim(),
        filters: currentFilters,
        sortConfig: currentSort,
        searchText: currentSearch || '',
        tags: []
      }

      const response = await apiClient.post('/saved-views', viewData)
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to save view')
      }

      // Refresh views list
      await fetchViews()
      
      // Close dialog and reset form
      setSaveDialogOpen(false)
      setSaveName('')
      setSaveDescription('')
      setSaveError(null)

    } catch (err) {
      console.error('Error saving view:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save view')
    }
  }

  const handleLoadView = (view: SavedView) => {
    onLoadView(view)
    setAnchorEl(null)
    setSelectedView(null)
  }

  const handleSetDefault = async (view: SavedView) => {
    try {
      const response = await apiClient.post(`/saved-views/${view.id}/set-default`)

      await fetchViews()
      setAnchorEl(null)
      setSelectedView(null)
    } catch (err) {
      console.error('Error setting default view:', err)
      setError(err instanceof Error ? err.message : 'Failed to set default view')
    }
  }

  const handleDeleteView = async (view: SavedView) => {
    if (!window.confirm(`Are you sure you want to delete "${view.name}"?`)) {
      return
    }

    try {
      const response = await apiClient.delete(`/saved-views/${view.id}`)

      await fetchViews()
      setAnchorEl(null)
      setSelectedView(null)
    } catch (err) {
      console.error('Error deleting view:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete view')
    }
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, view?: SavedView) => {
    setAnchorEl(event.currentTarget)
    setSelectedView(view || null)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedView(null)
  }

  const hasActiveFilters = Object.keys(currentFilters).length > 0 || currentSearch

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Saved Views Dropdown - Primary Action */}
      {loading ? (
        <CircularProgress size={16} />
      ) : views.length > 0 ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<BookmarkIcon sx={{ fontSize: 16 }} />}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 14 }} />}
          onClick={(e) => handleMenuClick(e)}
          sx={{
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            py: 0.5,
            px: 1.25,
            height: 32,
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'primary.50'
            }
          }}
        >
          {views.length} saved
        </Button>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<BookmarkBorderIcon sx={{ fontSize: 16 }} />}
          disabled
          sx={{
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.25,
            height: 32,
            color: 'text.disabled',
            borderColor: 'divider'
          }}
        >
          No views
        </Button>
      )}

      {/* Save Current View Button - Secondary Action */}
      <Button
        variant="text"
        size="small"
        startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
        onClick={() => setSaveDialogOpen(true)}
        disabled={!hasActiveFilters}
        sx={{
          minWidth: 'auto',
          textTransform: 'none',
          fontSize: '0.75rem',
          py: 0.5,
          px: 1,
          height: 32,
          color: hasActiveFilters ? 'text.primary' : 'text.disabled',
          '&:hover': {
            backgroundColor: hasActiveFilters ? 'action.hover' : 'transparent'
          },
          '&:disabled': {
            color: 'text.disabled'
          }
        }}
      >
        Save
      </Button>

      {/* Saved Views List Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !selectedView}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 280,
          },
        }}
      >
        {views.map((view) => (
          <MenuItem key={view.id} onClick={() => handleLoadView(view)} sx={{ py: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
                  {view.isDefault && <StarIcon sx={{ fontSize: 14 }} color="primary" />}
                  <Typography variant="body2" fontWeight={view.isDefault ? 'bold' : 'normal'} sx={{ fontSize: '0.8rem' }}>
                    {view.name}
                  </Typography>
                </Box>
                {view.description && (
                  <Typography variant="caption" color="text.secondary">
                    {view.description}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleMenuClick(e, view)
                }}
                sx={{ ml: 1 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* View Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedView)}
        onClose={handleMenuClose}
      >
        {selectedView && (
          <>
            <MenuItem onClick={() => handleLoadView(selectedView)} sx={{ py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <BookmarkIcon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}>Load View</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => handleSetDefault(selectedView)} sx={{ py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {selectedView.isDefault ? (
                  <StarIcon sx={{ fontSize: 16 }} color="primary" />
                ) : (
                  <StarBorderIcon sx={{ fontSize: 16 }} />
                )}
              </ListItemIcon>
              <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}>
                {selectedView.isDefault ? 'Remove Default' : 'Set as Default'}
              </ListItemText>
            </MenuItem>

            <MenuItem onClick={() => handleDeleteView(selectedView)} sx={{ color: 'error.main', py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <DeleteIcon sx={{ fontSize: 16 }} color="error" />
              </ListItemIcon>
              <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Save View Dialog */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Save Current View</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="View Name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            
            <TextField
              label="Description (optional)"
              value={saveDescription}
              onChange={(e) => setSaveDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveView} variant="contained">Save View</Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default SavedViews
